// HU-31 · Parametrizar las reglas de negocio. F-07, Sprint 2.
//
// Dos reglas de evaluación que ILENDER ajusta SIN REDESPLEGAR (RNF-10): el catálogo de
// algoritmos de causalidad (RN-14 / RN-18) y el criterio de gravedad (RN-03).
//
// DOS CONTEXTOS, DOS ROUTERS, Y NO ES UN DESCUIDO. El catálogo de algoritmos es una tabla
// del contexto de EVALUACIÓN (`algoritmo_causalidad`) y se mantiene por
// `/evaluacion/algoritmos`; los parámetros versionados —cuál algoritmo llega
// preseleccionado, cuál es el criterio de gravedad— viven en `parametro` y se mantienen por
// `/administracion/parametros`. Cada contexto escribe SUS tablas. Esta pantalla solo los
// junta para que el administrador no tenga que saber dónde vive cada cosa.
//
// ⚠ LO QUE YA SE DECIDIÓ NO SE REESCRIBE (C3, C5). Cambiar el predeterminado no toca las
// evaluaciones guardadas: cada una COPIÓ código y versión al clasificar. Cambiar el
// criterio no reclasifica: cada decisión COPIÓ los criterios con los que se tomó. Esta
// pantalla lo dice donde toca, porque es la pregunta que se hace quien va a guardar.

import { pedir } from './cliente';
import type { RespuestaDelApi } from '../tipos';

export const CLAVE_PREDETERMINADO = 'algoritmo_causalidad_predeterminado';
export const CLAVE_CRITERIO_GRAVEDAD = 'criterio_gravedad';
// HU-23-C8 / RN-19: horas hábiles del Comité para el FAR-03; global, no por autoridad.
export const CLAVE_PLAZO_INTERNO = 'plazo_interno_horas_habiles';

export type Algoritmo = {
  codigo: string;
  nombre: string;
  version: string;
  estado: string;
  nota: string | null;
  activo: boolean;
};

/** Cómo se lee cada estado del catálogo. `vigente` es decisión del Comité; el resto no. */
export const ESTADOS_DE_ALGORITMO: Record<string, string> = {
  propuesto: 'Propuesto',
  por_evaluar: 'Por evaluar',
  por_definir: 'Por definir',
  vigente: 'Vigente',
};

/**
 * Los cuatro estados que el Comité puede asignar, EN EL ORDEN en que se ofrecen (es el
 * recorrido natural: propuesto → por evaluar → por definir → vigente). Las etiquetas
 * salen de `ESTADOS_DE_ALGORITMO`, para que el desplegable y la tabla digan lo mismo.
 */
export const ESTADOS_DEL_COMITE: { value: string; label: string }[] = (['propuesto', 'por_evaluar', 'por_definir', 'vigente'] as const)
  .map((estado) => ({ value: estado, label: ESTADOS_DE_ALGORITMO[estado] }));

export function listarAlgoritmos() {
  return pedir<Algoritmo[]>('/evaluacion/algoritmos');
}

/** HU-31-C1. Entra como `propuesto`: que exista significa que se puede ELEGIR, no que se impone. */
export function agregarAlgoritmo(algoritmo: { codigo: string; nombre: string; version?: string; nota?: string | null }) {
  return pedir<Algoritmo>('/evaluacion/algoritmos', { metodo: 'POST', cuerpo: algoritmo });
}

/**
 * El estado del Comité sobre un algoritmo del catálogo. Es INFORMACIÓN, no regla: no
 * cambia qué algoritmo llega preseleccionado (eso es `CLAVE_PREDETERMINADO`) ni retira el
 * algoritmo. El backend exige motivo y rechaza (422) el mismo estado que ya tiene.
 */
export function cambiarEstadoDeAlgoritmo(codigo: string, estado: string, motivo: string) {
  return pedir<Algoritmo>(`/evaluacion/algoritmos/${encodeURIComponent(codigo)}/estado`, {
    metodo: 'PATCH',
    cuerpo: { estado, motivo },
  });
}

/** La versión vigente de un parámetro (`GET /parametros/{clave}` devuelve una sola). */
export function verParametro(clave: string) {
  return pedir<RespuestaDelApi>(`/administracion/parametros/${encodeURIComponent(clave)}`);
}

export function historialDeParametro(clave: string) {
  return pedir<RespuestaDelApi>(`/administracion/parametros/${encodeURIComponent(clave)}/historial`);
}

/**
 * HU-31-C2 / C4. Crea una VERSIÓN nueva; la anterior queda en el historial con quién y
 * cuándo (C6). `motivo` es lo que permite explicar, dos años después, por qué dos casos
 * parecidos se resolvieron distinto.
 */
export function cambiarParametro(clave: string, valor: unknown, motivo: string) {
  return pedir<RespuestaDelApi>(`/administracion/parametros/${encodeURIComponent(clave)}`, {
    metodo: 'PUT',
    cuerpo: { valor, motivo },
  });
}

// ---------------------------------------------------------------------------------
// AJ-EP (MG-ILE-918) · tarea 918-2. Los parámetros que hasta hoy solo se cambiaban por API.
//
// `GET /administracion/parametros` (endpoint 57) entrega, por parámetro editable, su etiqueta,
// unidad, tipo, valor vigente, el valor ya escrito para leer («8», «20 MB», «sin valor») y su
// estado declarado. La pantalla los lista sin conocerlos de antemano; la validación es la del
// catálogo del servidor (`administracion/dominio/parametros.py`) y su 422 trae el texto.
// ---------------------------------------------------------------------------------

/** `entero` · `lista_de_textos` · `topes_por_campo` · `horario` (catálogo del servidor). */
export type TipoDeParametro = 'entero' | 'lista_de_textos' | 'topes_por_campo' | 'horario' | string;

export type ParametroEditable = {
  clave: string;
  etiqueta: string;
  unidad: string | null;
  tipo: TipoDeParametro;
  valor_vigente: unknown;
  texto_del_valor: string;
  estado: string | null;
};

/** Una versión del historial; `texto` es la línea de AJ-EP-C3 («8 → 10 · R. Paredes · … · motivo»). */
export type VersionDeParametro = {
  version_id: string;
  clave: string;
  valor: unknown;
  vigente_desde: string;
  vigente_hasta: string | null;
  cambiado_por: string | null;
  motivo: string | null;
  texto: string;
};

export function listarParametros() {
  return pedir<ParametroEditable[]>('/administracion/parametros');
}

